import React, { useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import useMediaQuery from 'react-use-media-query-hook';
import { find } from 'lodash';

import { Tab } from '@headlessui/react';
import UnitEconomicsTab from '../../main/components/FinanceTabs/UnitEconomicsTab';
import GraphTab from '../../main/components/FinanceTabs/GraphTab';
import ForecastTab from '../../main/components/FinanceTabs/ForecastTab';
import {
  ARCHITECTURAL_MINIMAL_TEMPLATE_NAME,
  BLOG_FRIENDLY_TEMPLATE_NAME,
  BOLD_TEMPLATE_NAME,
  COLOURFUL_TEMPLATE_NAME,
  CURRENT_TEMPLATE_NAME,
  COLORS,
} from '../../main/utils/const';
import { getColorDependsOnBackground } from '../../main/utils/helpers/getColorDependsOnBackground';
import { getColorDependsOnBackgroundSummary } from '../../main/utils/helpers/portfolioCurrentTemplate/getColorDependsOnBackgroundSummary';
import { getCurrentCanvasIcon } from '../../main/utils/helpers/getCurrentCanvasIcon';
import SectionHeaderWeb from '../../main/components/SectionHeaderWeb';
import UploadFinancialFiles from '../../main/components/UploadFinancialFiles';
import { getColorTemplate } from '../../main/utils/helpers/getColorTemplate';
import Title from '../../main/components/Title';

const UnitEconomics = 0;
const Forecast = 1;
const Graph = 2;

const Finance = ({
  locales,
  locales: { portfolio, portfolioWeb },
  className,
  sectionHeaderTitleColor,
  descriptionColor,
  backgroundRow,
  borderVerticalTableColor,
  backgroundForecastTable,
  borderCardColor,
  backgroundUnitSvg,
  borderVerticalTableUnitColor,
  totalColor,
  // styleColor,
  tabColor,
  activeTabColor,
  firstChartColor,
  secondChartColor,
  firstSvgColor,
  secondSvgColor,
  separatorColor,
  backgroundChart,
  titleUnitColor,
  secondTitleUnitColor,
  titleForecastColor,
  secondTitleForecast,
  textForecastColor,
  titleChartColor,
  backgroundUnitTable,
  backgroundSection,
  tableTitleUnitColor,
  textUnitColor,
  textChartColor,
  templateName,
  fileBackgroundColor,
  isFourthColorPaletteNumber = false,
  colorStyle,
  isFirstColorPallete = false,
  colorPaletteNumber,
}) => {
  const [totalCashIn, setTotalCashIn] = useState([]);
  const [totalCashOut, setTotalCashOut] = useState([]);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [activeTab, setActiveTab] = useState(UnitEconomics);
  const { finance } = useSelector((state) => state.portfolio);
  const {
    portfolioStyle: { templateSettings, activeTemplateName },
    portfolioTemplate: { collection },
  } = useSelector((state) => state);

  const textColorDependsOnBackground =
    isFourthColorPaletteNumber &&
    getColorDependsOnBackground(backgroundSection);

  const styleColor = getColorDependsOnBackgroundSummary(
    colorPaletteNumber,
    backgroundSection,
  );

  const { black, white, transparentWhite } = COLORS;

  const activeTemplate = find(collection, ['sysName', activeTemplateName]);
  const colorTemplate = getColorTemplate(
    templateSettings,
    activeTemplateName,
    activeTemplate,
  );

  const [firstColor, fourthColor] = colorTemplate;

  const tabs = {
    [UnitEconomics]: (
      <UnitEconomicsTab
        locales={portfolio}
        isPreview
        templateName={templateName}
        separatorColor={separatorColor}
        titleColor={titleUnitColor}
        secondTitleColor={secondTitleUnitColor}
        backgroundSvg={backgroundUnitSvg}
        borderCardColor={borderCardColor}
        verticalBorderColor={borderVerticalTableUnitColor}
        backgroundRow={backgroundRow}
        backgroundTable={backgroundUnitTable}
        tableTitleColor={tableTitleUnitColor}
        textColor={textUnitColor}
      />
    ),
    [Forecast]: (
      <ForecastTab
        locales={portfolio}
        setTotalCashIn={setTotalCashIn}
        setTotalCashOut={setTotalCashOut}
        backgroundTable={backgroundForecastTable}
        backgroundRow={backgroundRow}
        totalColor={totalColor}
        firstSvgColor={firstSvgColor}
        secondSvgColor={secondSvgColor}
        borderCardColor={borderCardColor}
        borderVerticalTableColor={borderVerticalTableColor}
        titleColor={titleForecastColor}
        secondTitleColor={secondTitleForecast}
        textColor={textForecastColor}
        templateName={templateName}
      />
    ),
    [Graph]: (
      <GraphTab
        locales={portfolio}
        totalCashIn={totalCashIn}
        totalCashOut={totalCashOut}
        firstChartColor={firstChartColor}
        secondChartColor={secondChartColor}
        background={backgroundChart}
        borderCardColor={borderCardColor}
        titleColor={titleChartColor}
        textColor={textChartColor}
        templateName={templateName}
      />
    ),
  };
  const allTabs = [tabs[UnitEconomics], tabs[Forecast], tabs[Graph]];

  const isCurrentTemplate = templateName === CURRENT_TEMPLATE_NAME;

  const getFinanceOnTemplate = () => {
    switch (templateName) {
      case CURRENT_TEMPLATE_NAME: {
        return (
          <div className="margin-wide-40-lg">
            <UploadFinancialFiles
              isPreview
              disabled
              locales={locales}
              fileTextColor={styleColor}
              fileBorderColor={styleColor}
              uplouderButtonStyle="portfolio-file-uploader__current"
              fileStyle="portfolio-file-uploader-file__title"
              borderButtonColor={styleColor}
              textButtonColor={styleColor}
            />
          </div>
        );
      }
      case COLOURFUL_TEMPLATE_NAME: {
        return (
          <div className="margin-wide-40-lg margin-top-30">
            <UploadFinancialFiles
              isPreview
              disabled
              locales={locales}
              fileBackgroundColor={fileBackgroundColor}
              fileTextColor={
                isFourthColorPaletteNumber &&
                getColorDependsOnBackground(fileBackgroundColor)
              }
              uplouderButtonStyle="portfolio-file-uploader__colourful"
              filesWrapperStyle="portfolio-file-uploader-file-colourful"
              fileStyle="portfolio-file-uploader-file-colourful-link"
              removeIconStyle="portfolio-file-uploader-file__remove-colourful"
              borderButtonColor={colorStyle}
              textButtonColor={colorStyle}
            />
          </div>
        );
      }
      case BLOG_FRIENDLY_TEMPLATE_NAME: {
        return (
          <div className="margin-wide-40-lg px-[190px]">
            <UploadFinancialFiles
              isPreview
              disabled
              locales={locales}
              uplouderButtonStyle="portfolio-file-uploader__blog"
              fileStyle="portfolio-file-uploader-file-blog"
              removeIconStyle="portfolio-file-uploader-file__remove-blog"
              textButtonColor={textColorDependsOnBackground}
              fileTextColor={textColorDependsOnBackground}
              borderButtonColor={textColorDependsOnBackground}
              fileBorderColor={textColorDependsOnBackground}
            />
          </div>
        );
      }
      case ARCHITECTURAL_MINIMAL_TEMPLATE_NAME: {
        return (
          <div className="margin-wide-40-lg px-[190px]">
            <UploadFinancialFiles
              isPreview
              disabled
              locales={locales}
              uplouderButtonStyle="portfolio-file-uploader__arch"
              fileStyle="portfolio-file-uploader-file-arch-link"
              removeIconStyle="portfolio-file-uploader-file__remove-arch"
              fileTextColor={textColorDependsOnBackground === white && white}
              fileBackgroundColor={
                textColorDependsOnBackground === white
                  ? transparentWhite
                  : black
              }
              fileBorderColor={
                textColorDependsOnBackground === white ? white : black
              }
              borderButtonColor={textColorDependsOnBackground}
              textButtonColor={textColorDependsOnBackground}
            />
          </div>
        );
      }
      case BOLD_TEMPLATE_NAME: {
        return (
          <UploadFinancialFiles
            isPreview
            disabled
            locales={locales}
            fileBackgroundColor={firstColor}
            fileTextColor={fourthColor}
            style={{ padding: '0px !important' }}
            uplouderButtonStyle="portfolio-file-uploader__bold ml-auto mr-0"
            filesWrapperStyle="portfolio-file-uploader-file-bold"
            fileStyle="portfolio-file-uploader-file-bold-link"
            removeIconStyle="portfolio-file-uploader-file__remove-bold"
            borderButtonColor={firstColor}
            textButtonColor={firstColor}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div className={className} style={{ backgroundColor: backgroundSection }}>
      {isCurrentTemplate && getCurrentCanvasIcon('FinanceCanvas', styleColor)}
      <SectionHeaderWeb
        templateName={templateName}
        title={portfolioWeb.finance.title}
        description={finance.description}
        className={isCurrentTemplate && 'violet-title'}
        style={{ color: sectionHeaderTitleColor }}
        descriptionColor={descriptionColor}
      />

      {!isMobile && (
        <Tab.Group>
          <Tab.List className="tabs-wrapper">
            <Tab
              className={classNames('tab', 'custom-font-body', {
                active: activeTab === UnitEconomics,
              })}
              onClick={() => setActiveTab(UnitEconomics)}
              style={{
                color: activeTab === UnitEconomics ? activeTabColor : tabColor,
              }}
            >
              {portfolio.finance.tabName}
            </Tab>
            <Tab
              className={classNames('tab', 'custom-font-body', {
                active: activeTab === Forecast,
              })}
              onClick={() => setActiveTab(Forecast)}
              id="docx-forecast-tab"
              style={{
                color: activeTab === Forecast ? activeTabColor : tabColor,
              }}
            >
              {portfolio.finance.forecast.title}
            </Tab>
            <Tab
              className={classNames('tab', 'custom-font-body', {
                active: activeTab === Graph,
              })}
              onClick={() => setActiveTab(Graph)}
              id="docx-graph-tab"
              style={{
                color: activeTab === Graph ? activeTabColor : tabColor,
              }}
            >
              {portfolio.finance.chart.title}
            </Tab>
          </Tab.List>
          <Tab.Panels className="w-full h-full">
            <Tab.Panel>{tabs[0]}</Tab.Panel>
            <Tab.Panel>{tabs[1]}</Tab.Panel>
            <Tab.Panel>{tabs[2]}</Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      )}

      {isMobile && allTabs}

      <Title
        text={portfolio.finance.fileUpload.title}
        className={classNames('custom-font-title', {
          'text-black': !isFirstColorPallete && !isFourthColorPaletteNumber,
        })}
        style={{ color: textColorDependsOnBackground }}
      />

      {getFinanceOnTemplate()}
    </div>
  );
};

export default Finance;
